<template>
  <v-chip
    small
    :outlined="outlined"
    class="category-badge"
    :class="computedClass"
    :style="computedStyle"
    :aria-label="ariaLabel || `Category: ${category}`"
    :data-testid="`category-badge-${sanitizedCategory}`"
  >
    {{ category }}
  </v-chip>
</template>

<script lang="ts">
import Vue from 'vue'

export default Vue.extend({
  name: 'CategoryBadge',

  props: {
    /** Category label to display */
    category: {
      type: String,
      required: true
    },
    /** Optional colour override (CSS colour value or Vuetify colour class) */
    color: {
      type: String,
      default: ''
    },
    /** Display as outlined chip */
    outlined: {
      type: Boolean,
      default: true
    },
    /** Custom aria-label for accessibility */
    ariaLabel: {
      type: String,
      default: ''
    },
    /** Auto-generate colour from category name */
    autoColor: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    sanitizedCategory(): string {
      return this.category.toLowerCase().replace(/\s+/g, '-').replace(/[&]/g, 'and')
    },

    computedClass(): string {
      return `category-badge--${this.sanitizedCategory}`
    },

    computedStyle(): Record<string, string> {
      if (this.color) {
        // Check if it's a CSS colour (starts with #, rgb, hsl, or common colour names)
        const isCssColor = /^(#|rgb|hsl|[a-z]+$)/i.test(this.color)
        if (isCssColor) {
          return this.outlined
            ? { color: `${this.color} !important`, borderColor: `${this.color} !important` }
            : { backgroundColor: this.color, color: 'white' }
        }
      } else if (this.autoColor) {
        // Generate colour based on category name hash
        const colour = this.getCategoryColor(this.category)
        return this.outlined
          ? { color: `${colour} !important`, borderColor: `${colour} !important` }
          : { backgroundColor: colour, color: 'white' }
      }
      return {}
    },

    getCategoryColor() {
      return (category: string): string => {
        // Simple hash function to generate consistent colours
        // Using lower saturation and darker lightness for professional appearance
        let hash = 0
        for (let i = 0; i < category.length; i++) {
          hash = category.charCodeAt(i) + ((hash << 5) - hash)
        }
        const hue = hash % 360
        return `hsl(${hue}, 35%, 45%)`
      }
    }
  }
})
</script>

<style scoped>
.category-badge {
  font-weight: 500;
  font-size: var(--font-size-xs);
  border-width: 1px;
}

/* Default category-specific colours using CSS variables for dark mode support */
.category-badge--contacts-and-preferences {
  color: var(--info) !important;
  border-color: var(--info) !important;
}

.category-badge--delete {
  color: var(--danger) !important;
  border-color: var(--danger) !important;
}

.category-badge--rtbf {
  color: var(--warning) !important;
  border-color: var(--warning) !important;
}

.category-badge--merge {
  color: var(--muted-foreground) !important;
  border-color: var(--border) !important;
}

.category-badge--primary {
  color: var(--primary) !important;
  border-color: var(--primary) !important;
}

.category-badge--secondary {
  color: var(--secondary-foreground) !important;
  border-color: var(--secondary) !important;
}

.category-badge--success {
  color: var(--success) !important;
  border-color: var(--success) !important;
}

.category-badge--warning {
  color: var(--warning) !important;
  border-color: var(--warning) !important;
}

.category-badge--error {
  color: var(--danger) !important;
  border-color: var(--danger) !important;
}

.category-badge--info {
  color: var(--info) !important;
  border-color: var(--info) !important;
}
</style>
